import { CacheService, LogService } from '@absg/angular-common';
import { StationApi, StationDetails, StationDetailsApi, StationInfoApi, StationLoadRequest, StationLoadResponse } from '@absg/api-station-management';
import { BaseService, CacheKeys, StorageStation } from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class StorageStationService extends BaseService {

  constructor(
    private stationDetailApi: StationDetailsApi,
    private stationInfoApi: StationInfoApi,
    private stationApi: StationApi,
    loggingService: LogService,
    private cacheService: CacheService,
  ) {
    super(loggingService);
  }

  getStationDetails(hostName: string): Observable<StationDetails> {
    const stationKey = `${CacheKeys.stationDetailsPartialKey}${hostName}`;
    return this.stationDetailApi.getStationInfoDetails(hostName)
      .pipe(
        map((stationDetails) => {
          this.cacheService.setWithExpiry(stationKey, stationDetails, CacheKeys.eightHoursInMinutes);
          return stationDetails;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<StationDetails>;
  }

  // TODO: Replace with getStationInfoDetailsForLoading
  getStationDetailsForLoading(hostName: string): Observable<StationDetails> {
    const stationKey = `${CacheKeys.stationDetailsPartialKey}${hostName}`;
    const foundCacheVersion = this.cacheService.getWithExpiry(stationKey);
    if (foundCacheVersion.value) {
      return of(foundCacheVersion.value);
    } else {
      return this.stationDetailApi.getStationInfoDetailsByStationName(hostName)
        .pipe(
          map((stationDetails) => {
            this.cacheService.setWithExpiry(stationKey, stationDetails, CacheKeys.eightHoursInMinutes);
            return stationDetails;
          }),
          catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<StationDetails>;
    }
  }

  getStationInfoDetailsForLoading(hostName: string, stationId: number): Observable<StationLoadResponse> {
    const configKey = `${CacheKeys.stationConfigPartialKey}${hostName}`;
    // TODO: replace station details caching
    // const stationKey = `${CacheKeys.stationDetailsPartialKey}${hostName}`;
    // const foundCacheVersion = this.cacheService.getWithExpiry(stationKey);
    const request = { stationId, stationName: hostName, isStationDefaultsNeeded: false, isStationDetailsNeeded: false } as StationLoadRequest;
    return this.stationInfoApi.getStationLoadDetails(request).pipe(
      map((stationDetails) => {
        // this.cacheService.setWithExpiry(stationKey, stationDetails.StationDetails, CacheKeys.eightHoursInMinutes);
        this.cacheService.set(configKey, stationDetails.stationConfigRules);
        return stationDetails;
      }),
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<StationLoadResponse>;
  }

  saveStation(station: StorageStation): Observable<StorageStation> {
    if (station.id) {
      // Update
      return this.stationApi.updateStation(station)
        .pipe(
          map(response => response as StorageStation),
          catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<StorageStation>;
    } else {
      // Create
      return this.stationApi.createStation(station)
        .pipe(
          map(response => response as StorageStation),
          catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<StorageStation>;
    }
  }

  sortListBySequence(response: StorageStation[]): StorageStation[] {
    return response.sort((i1, i2) => i1.sequence < i2.sequence ? -1 : 1);
  }

  sortListById(response: StorageStation[]): StorageStation[] {
    return response.sort((i1, i2) => i1.id < i2.id ? -1 : 1);
  }
}
